import React, { useState, useEffect } from 'react';
import type { BanType } from '../../../types/ban';
import {
  parseAsn,
  validateAsn,
  validateDomain,
  validateIpAddress,
  validateIsp,
} from '../../../utils/validation/banValidation';

interface AdminBanFormModalProps {
  type: BanType;
  ban: any | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
}

export default function AdminBanFormModal({
  type,
  ban,
  onClose,
  onSubmit,
  isSubmitting,
}: AdminBanFormModalProps) {
  const [formData, setFormData] = useState({
    value: '',
    asn_name: '',
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ban) {
      setFormData({
        value: getValueFromBan(ban),
        asn_name: ban.asn_name || '',
        reason: ban.reason || '',
      });
    }
  }, [ban, type]);

  const getValueFromBan = (ban: any): string => {
    switch (type) {
      case 'ip':
        return ban.ip_address;
      case 'domain':
        return ban.domain;
      case 'isp':
        return ban.isp_name;
      case 'asn':
        return ban.asn.toString();
      default:
        return '';
    }
  };

  const getFieldLabel = (): string => {
    switch (type) {
      case 'ip':
        return 'IP Address';
      case 'domain':
        return 'Domain';
      case 'isp':
        return 'ISP Name';
      case 'asn':
        return 'ASN';
    }
  };

  const getPlaceholder = (): string => {
    switch (type) {
      case 'ip':
        return 'e.g., 192.168.1.1';
      case 'domain':
        return 'e.g., example.com';
      case 'isp':
        return 'e.g., Google Cloud';
      case 'asn':
        return 'e.g., AS15169 or 15169';
    }
  };

  const validateValue = (value: string): string | null => {
    switch (type) {
      case 'ip':
        return validateIpAddress(value);
      case 'domain':
        return validateDomain(value);
      case 'isp':
        return validateIsp(value);
      case 'asn':
        return validateAsn(value);
      default:
        return 'Invalid type';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate value only when creating new ban
    if (!ban) {
      const validationError = validateValue(formData.value);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Prepare data
    const data: any = {};

    if (!ban) {
      switch (type) {
        case 'ip':
          data.ip_address = formData.value;
          break;
        case 'domain':
          data.domain = formData.value;
          break;
        case 'isp':
          data.isp_name = formData.value;
          break;
        case 'asn':
          const asnNumber = parseAsn(formData.value);
          if (asnNumber === null) {
            setError('Invalid ASN format');
            return;
          }
          data.asn = asnNumber;
          if (formData.asn_name.trim()) {
            data.asn_name = formData.asn_name.trim();
          }
          break;
      }
    }

    // Add ASN name for updates
    if (ban && type === 'asn' && formData.asn_name.trim()) {
      data.asn_name = formData.asn_name.trim();
    }

    // Add reason if provided
    if (formData.reason.trim()) {
      data.reason = formData.reason.trim();
    }

    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {ban ? 'Edit' : 'Add'} {type.toUpperCase()} Ban
              </h2>
              <p className="text-xs text-red-600 mt-1">
                ⚠️ GLOBAL - Affects all accounts
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Value field - only show when creating */}
            {!ban && (
              <div>
                <label htmlFor="value" className="label">
                  {getFieldLabel()} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="value"
                  value={formData.value}
                  onChange={(e) => {
                    setFormData({ ...formData, value: e.target.value });
                    setError(null);
                  }}
                  placeholder={getPlaceholder()}
                  className="input w-full"
                  required
                  disabled={isSubmitting}
                />
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
              </div>
            )}

            {/* Show current value when editing */}
            {ban && (
              <div>
                <label className="label">{getFieldLabel()}</label>
                <div className="px-3 py-2 bg-gray-100 rounded border border-gray-300">
                  <code className="text-sm">{formData.value}</code>
                </div>
              </div>
            )}

            {/* ASN Name field (for ASN type only) */}
            {type === 'asn' && (
              <div>
                <label htmlFor="asn_name" className="label">
                  ASN Name (optional)
                </label>
                <input
                  type="text"
                  id="asn_name"
                  value={formData.asn_name}
                  onChange={(e) =>
                    setFormData({ ...formData, asn_name: e.target.value })
                  }
                  placeholder="e.g., Google LLC"
                  className="input w-full"
                  maxLength={255}
                  disabled={isSubmitting}
                />
              </div>
            )}

            {/* Reason field */}
            <div>
              <label htmlFor="reason" className="label">
                Reason (optional)
              </label>
              <textarea
                id="reason"
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                placeholder={`Why is this ${type} being banned?`}
                className="input w-full"
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.reason.length}/500 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {ban ? 'Updating...' : 'Adding...'}
                  </>
                ) : ban ? (
                  'Update Ban'
                ) : (
                  'Add Ban'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
